//@flow
import React from 'react';
import {Link} from 'react-router-dom';
import {
  Button,
  Typography,
  makeStyles,
  FormControlLabel,
} from '@material-ui/core';

import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {PhonelinkEraseOutlined, RefreshOutlined} from '@material-ui/icons';
import {colors} from '../../../../assets/styles/colors';

import type {DeviceState, StoreDevice} from '../../../../entities';
import {getMultichatServiceIcon} from '../../utils/service-icon';
import IosSwitch from '../../../../elements/IosSwitch/IosSwitch';
import BotIcon from '../../../../elements/icons/BotIcon';
import {useChatBotDetail} from '../utils/conversation';
import {formatStoreDeviceIdentifier, getServiceLabel} from '../utils';

const useStyles = makeStyles(theme => ({
  channelItem: {
    display: 'flex',

    justifyContent: 'space-between',
    borderBottom: `1px solid ${colors.grey300}`,
    flexDirection: 'column',

    '&:first-child': {
      borderTop: `1px solid ${colors.grey300}`,
    },
    '&:last-child': {
      borderBottom: 'none',
    },

    [theme.breakpoints.up('sm')]: {
      alignItems: 'center',
      flexDirection: 'row',
    },
  },
  channelButton: {
    textTransform: 'none',
    padding: '.75rem 1rem',
    flexGrow: 1,
    color: colors.grey900,

    '&:hover': {
      backgroundColor: 'transparent',
      color: colors.primary,

      '& img': {
        transform: 'scale(1.2)',
      },
    },
  },
  channelButtonLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '.75rem',
    width: '100%',
  },
  channelIcon: {
    width: '2.5rem',
    height: '2.5rem',

    '& img': {
      transition: 'transform .2s ease-in-out',
      width: '100%',
      height: '100%',
    },
  },
  channelInfo: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  channelName: {
    fontSize: '1rem',
    fontWeight: 600,
    transition: 'color .2s ease-in-out',
    color: 'inherit',
  },
  channelId: {
    fontSize: '.85rem',
    fontWeight: 500,
    color: colors.grey700,
  },
  channelState: {
    fontSize: '.8rem',
    fontWeight: 600,
    color: colors.grey700,

    '&[data-state="CONNECTED"]': {
      color: colors.success,
    },

    '&[data-state="DISCONNECTED"]': {
      color: colors.error,
    },

    '&[data-state="ARCHIVED"]': {
      color: '#fbaf19',
    },
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '.25rem',
  },
  removeChannelLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    color: colors.error,
  },
  removeChannelButton: {
    width: 'min-content',
    justifyContent: 'flex-start',
  },
  reconnectChannelLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    color: colors.primary,
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    color: colors.grey500,

    '&[data-enabled="true"]': {
      color: colors.success,
    },
  },
}));

type PropEvents = {
  onDelete: (id: string) => void,
  onReconnect: (id: string) => void,
};

type Props = {
  device: StoreDevice,
} & PropEvents;

const formatChannelState = (state: DeviceState) => {
  if (state === 'CONNECTED') {
    return 'Conectado';
  }

  if (state === 'CONNECTING') {
    return 'Conectando';
  }

  if (state === 'ARCHIVED') {
    return 'Archivado';
  }

  return 'Desconectado';
};

export default function ChatDeviceItem({device, onDelete, onReconnect}: Props) {
  const classes = useStyles();

  const icon = getMultichatServiceIcon(device.service);
  const serviceLabel = getServiceLabel(device.service);
  const channelId = formatStoreDeviceIdentifier(device);

  const {chatBot, loading, toggleChatBotEnabled} = useChatBotDetail(
    device.chatbotId
  );

  return (
    <li className={classes.channelItem}>
      <Button
        className={classes.channelButton}
        to="/chat/multi-chat"
        component={Link}
      >
        <div className={classes.channelButtonLabel}>
          <picture className={classes.channelIcon}>
            <img src={icon} alt={device.service} />
          </picture>

          <div className={classes.channelInfo}>
            <Typography className={classes.channelName}>
              {serviceLabel}
            </Typography>
            <Typography className={classes.channelId}>{channelId}</Typography>
            <Typography
              className={classes.channelState}
              data-state={device.state}
            >
              Estado: {formatChannelState(device.state)}
            </Typography>
          </div>
        </div>
      </Button>
      <div className={classes.buttons}>
        {device.chatbotId && (
          <FormControlLabel
            control={
              <IosSwitch
                checked={!!chatBot?.enabled}
                disabled={loading}
                onChange={e => toggleChatBotEnabled(e.target.checked)}
              />
            }
            labelPlacement="start"
            label={
              <div className={classes.label} data-enabled={!!chatBot?.enabled}>
                <BotIcon width="24px" height="24px" />
                <span>
                  Bot:
                  {chatBot?.enabled ? ' Activado' : ' Desactivado'}
                </span>
              </div>
            }
          />
        )}
        {device.state === 'DISCONNECTED' && (
          <CustomButton
            variant="text"
            className={classes.removeChannelButton}
            onClick={e => {
              e.stopPropagation();
              onReconnect(device.id);
            }}
          >
            <div className={classes.reconnectChannelLabel}>
              <RefreshOutlined fontSize="small" />
              <Typography>Reconectar dispositivo</Typography>
            </div>
          </CustomButton>
        )}

        <CustomButton
          variant="text"
          className={classes.removeChannelButton}
          onClick={e => {
            e.stopPropagation();
            onDelete(device.id);
          }}
        >
          <div className={classes.removeChannelLabel}>
            <PhonelinkEraseOutlined fontSize="small" />
            <Typography>Desconectar dispositivo</Typography>
          </div>
        </CustomButton>
      </div>
    </li>
  );
}
